import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { HelpTooltip } from '@gonfalon/launchpad-experimental/HelpTooltip';
import { Checkbox, FieldGroup } from '@launchpad-ui/components';

import type { MetricForm, WindowUnit } from '../formUtils';
import { WindowFieldsRow } from '../WindowFieldsRow/WindowFieldsRow';
import { getEndError, getStartError, WINDOW_UNITS } from '../windowingUtils';

import styles from './MetricWindowingParams.module.css';

interface MetricWindowingParamsProps {
  context: UseFormReturn<MetricForm>;
}

export const MetricWindowingParams: React.FC<MetricWindowingParamsProps> = ({ context }) => {
  const { control, register, setValue, unregister, formState } = context;

  const windowMode = useWatch({ control, name: 'windowMode' }) ?? 'none';
  const windowStart = useWatch({ control, name: 'windowStart' });
  const windowEnd = useWatch({ control, name: 'windowEnd' });
  const windowUnit = useWatch({ control, name: 'windowUnit' }) ?? 'days';

  const isCustom = windowMode === 'custom';
  const unitDef = WINDOW_UNITS.find((u) => u.id === windowUnit) ?? WINDOW_UNITS[0];
  const unitLabel = unitDef.label.toLowerCase();
  const maxInUnit = unitDef.maxInUnit;

  const startError = isCustom ? getStartError(windowStart, maxInUnit, unitLabel) : undefined;
  const endError = isCustom ? getEndError(windowEnd, maxInUnit, unitLabel, windowStart) : undefined;

  const startInteracted =
    Boolean(formState.touchedFields.windowStart || formState.dirtyFields.windowStart) || formState.isSubmitted;
  const endInteracted =
    Boolean(formState.touchedFields.windowEnd || formState.dirtyFields.windowEnd) || formState.isSubmitted;
  const showStartInvalid = Boolean(startError) && startInteracted;
  const showEndInvalid = Boolean(endError) && endInteracted;

  const handleEnableChange = (isSelected: boolean) => {
    if (isSelected) {
      setValue('windowMode', 'custom', { shouldDirty: true });
      setValue('windowStart', undefined, { shouldDirty: true });
      setValue('windowEnd', undefined, { shouldDirty: true });
    } else {
      setValue('windowMode', 'none', { shouldDirty: true });
      unregister(['windowStart', 'windowEnd'], { keepError: false, keepDirty: false, keepTouched: false });
    }
  };

  return (
    <FieldGroup className={styles.windowingSection}>
      <div className={styles.labelRow}>
        <Checkbox isSelected={isCustom} onChange={handleEnableChange}>
          Enable custom measurement window
        </Checkbox>
        <HelpTooltip placement="right">
          Defines how long after first exposure metric events are included in experiment analysis. This setting applies
          only when the metric is used in an experiment.
        </HelpTooltip>
      </div>

      {isCustom && (
        <div className={styles.windowingContent}>
          <p className={styles.helpText}>Units appear in results only after their full window completes.</p>

          <WindowFieldsRow
            key={windowUnit}
            windowUnit={windowUnit}
            windowStart={windowStart}
            windowEnd={windowEnd}
            showStartInvalid={showStartInvalid}
            showEndInvalid={showEndInvalid}
            startError={startError}
            endError={endError}
            unitLabel={windowUnit}
            maxInUnit={maxInUnit}
            onUnitChange={(unit) => {
              setValue('windowUnit', unit as WindowUnit, { shouldDirty: true });
              unregister(['windowStart', 'windowEnd'], {
                keepError: false,
                keepDirty: false,
                keepTouched: false,
              });
            }}
            startInputProps={register('windowStart', {
              valueAsNumber: true,
              validate: (val) => getStartError(val, maxInUnit, unitLabel) ?? true,
            })}
            endInputProps={register('windowEnd', {
              valueAsNumber: true,
              validate: (val, formValues) => getEndError(val, maxInUnit, unitLabel, formValues.windowStart) ?? true,
            })}
          />
        </div>
      )}
    </FieldGroup>
  );
};

/** Convert form values to API millisecond offsets. Returns undefined for both when no window is set. */
export { toWindowOffsets } from '../windowingUtils';
